import { useEffect, type ChangeEvent, type Dispatch } from 'react'
import { useTranslation } from 'react-i18next'

import { Field, Typography } from '@matillion/component-library'
import classNames from 'classnames'

import { useListEnvironments } from 'api/hooks/useListEnvironments'

import type { JobVariableType, VariableScope } from 'job-lib/types/Variables'

import {
  Fields,
  ReducerActions,
  type FormState,
  type ProjectVariableType
} from 'modules/ManageVariables/types'

import classes from '../Form.module.scss'
import { type ReducerAction } from '../formReducer/formReducer'

export const ProjectSpecificFields = ({
  state,
  dispatch,
  selectedVariableScope,
  selectedVariableType,
  editing
}: {
  state: FormState
  dispatch: Dispatch<ReducerAction>
  selectedVariableScope: VariableScope
  selectedVariableType: JobVariableType | ProjectVariableType | null
  editing: boolean
}) => {
  const { t } = useTranslation()
  const { data: environments } = useListEnvironments()
  const { [Fields.DEFAULT_VALUE]: defaultValue, PROJECT_DEFAULT_OVERRIDES } =
    state

  useEffect(() => {
    if (selectedVariableType && editing) {
      dispatch({
        type: ReducerActions.UPDATE_DEFAULT_VALUE,
        field: Fields.DEFAULT_VALUE,
        value: defaultValue.value,
        selectedVariableScope,
        selectedVariableType
      })

      environments?.results.forEach((environment) => {
        const field = PROJECT_DEFAULT_OVERRIDES.value?.[environment.id]

        dispatch({
          type: ReducerActions.UPDATE_PROJECT_OVERRIDE,
          field: Fields.PROJECT_DEFAULT_OVERRIDES,
          environmentId: environment.id,
          value: field?.value,
          selectedVariableScope,
          selectedVariableType
        })
      })
    }
  }, [
    selectedVariableType,
    selectedVariableScope,
    defaultValue,
    PROJECT_DEFAULT_OVERRIDES.value,
    environments?.results,
    editing,
    dispatch
  ])
  return (
    <>
      <Field
        className={classes.Field}
        title={t('manageVariables.jobVariables.fields.defaultValue')}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          dispatch({
            type: ReducerActions.UPDATE_DEFAULT_VALUE,
            field: Fields.DEFAULT_VALUE,
            value: e.target.value,
            selectedVariableScope,
            selectedVariableType
          })
        }}
        value={state.DEFAULT_VALUE.value}
        hasError={state[Fields.DEFAULT_VALUE].isValid === false}
        errorText={
          state[Fields.DEFAULT_VALUE].isValid === false &&
          t(state[Fields.DEFAULT_VALUE].error, {
            field: t('manageVariables.jobVariables.fields.defaultValue')
          })
        }
      />

      <fieldset className={classes.Form__ScopeFieldset}>
        <legend className={classes.Form__Legend}>
          <Typography format="ts">
            {t(
              'manageVariables.jobVariables.fields.environmentDefaultOverride'
            )}
          </Typography>
        </legend>
        <div className={classes.Form__ProjectOverrideField}>
          <Typography
            aria-hidden
            format="bcs"
            weight="bold"
            className={classes.Form__CustomLabel}
          >
            <span>{t('manageVariables.jobVariables.fields.environment')}</span>
          </Typography>
          <Typography
            aria-hidden
            format="bcs"
            weight="bold"
            className={classes.Form__CustomLabel}
          >
            <span>{t('common.value')}</span>
          </Typography>
        </div>

        {environments?.results.map((environment) => {
          const field = state.PROJECT_DEFAULT_OVERRIDES.value?.[environment.id]

          return (
            <Field
              key={environment.id}
              className={classNames(classes.Field, classes.Form__FieldList)}
              onBlur={() => {
                dispatch({
                  type: ReducerActions.VALIDATE_FORM,
                  selectedVariableScope,
                  selectedVariableType
                })
              }}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                dispatch({
                  type: ReducerActions.UPDATE_PROJECT_OVERRIDE,
                  field: Fields.PROJECT_DEFAULT_OVERRIDES,
                  environmentId: environment.id,
                  value: e.target.value,
                  selectedVariableScope,
                  selectedVariableType
                })
              }}
              title={environment.name}
              value={field?.value ?? ''}
              hasError={field && !field?.isValid}
              errorText={!field?.isValid && t(field?.error ?? '')}
            />
          )
        })}
      </fieldset>
    </>
  )
}
